import React, { useEffect, useMemo, useState } from "react";
import {
    Loader2,
    Users,
    TrendingUp,
    Package,
    Briefcase,
    ShoppingBag,
    RefreshCw,
    Filter,
    Mail,
    Phone,
    MapPin,
    Calendar,
    Save,
    X,
    Pencil,
    ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { LEAD_STATUSES, LEAD_STATUS_LABELS, LEAD_STATUS_STYLES } from "../lib/leadStatus";

const LINE_COLORS = {
    fruit: { bg: "bg-brand-orange-soft", text: "text-brand-orange", label: "FRUIT" },
    detox: { bg: "bg-brand-green-soft", text: "text-brand-green", label: "DETOX" },
    protein: { bg: "bg-brand-pink", text: "text-[#CFA059]", label: "PROTEIN" },
    tea: { bg: "bg-brand-yellow", text: "text-[#8691A0]", label: "TEA" },
};

export default function AdminLeads({ apiAuth }) {
    const [stats, setStats] = useState(null);
    const [leads, setLeads] = useState([]);
    const [busy, setBusy] = useState(true);
    const [filterInterest, setFilterInterest] = useState("all");
    const [filterLine, setFilterLine] = useState("all");
    const [filterStatus, setFilterStatus] = useState("all");
    const [editingId, setEditingId] = useState(null);

    const load = async () => {
        setBusy(true);
        try {
            const params = new URLSearchParams();
            if (filterInterest !== "all") params.set("interest", filterInterest);
            if (filterLine !== "all") params.set("line_interest", filterLine);
            if (filterStatus !== "all") params.set("status", filterStatus);
            const qs = params.toString() ? `?${params.toString()}` : "";
            const [statsRes, leadsRes] = await Promise.all([
                apiAuth("/admin/stats"),
                apiAuth(`/admin/leads${qs}`),
            ]);
            setStats(statsRes.data);
            setLeads(leadsRes.data);
        } catch {
            toast.error("Erro ao carregar leads");
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterInterest, filterLine, filterStatus]);

    const updateLead = async (id, patch) => {
        try {
            const { data } = await apiAuth(`/admin/leads/${id}`, {
                method: "PATCH",
                data: patch,
            });
            setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
            toast.success("Lead atualizada");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro ao atualizar");
        }
    };

    // Pipeline counts (from currently visible leads)
    const pipelineCounts = useMemo(() => {
        const c = {};
        LEAD_STATUSES.forEach((s) => (c[s] = 0));
        leads.forEach((l) => {
            const s = l.status || "novo";
            c[s] = (c[s] || 0) + 1;
        });
        return c;
    }, [leads]);

    return (
        <div className="space-y-6" data-testid="admin-leads-panel">
            {/* Top stats */}
            <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    icon={Users}
                    label="Total de leads"
                    value={stats?.total_leads ?? "—"}
                    accent="text-brand-green"
                    bg="bg-white"
                    testid="stat-total"
                />
                <StatCard
                    icon={TrendingUp}
                    label="Últimos 7 dias"
                    value={stats?.recent ?? "—"}
                    accent="text-brand-orange"
                    bg="bg-brand-orange-soft"
                    testid="stat-recent"
                />
                <StatCard
                    icon={Briefcase}
                    label="Parceiros"
                    value={stats?.by_interest?.parceiro ?? 0}
                    accent="text-white"
                    bg="bg-brand-green"
                    white
                    testid="stat-parceiros"
                />
                <StatCard
                    icon={ShoppingBag}
                    label="Consumidores"
                    value={stats?.by_interest?.consumidor ?? 0}
                    accent="text-white"
                    bg="bg-brand-ink"
                    white
                    testid="stat-consumidores"
                />
            </section>

            {/* Pipeline */}
            <section className="rounded-2xl bg-white p-6 shadow-soft ring-1 ring-brand-green/10">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-brand-green mb-3">
                    <ClipboardList className="h-3.5 w-3.5" /> Pipeline
                </div>
                <div className="grid grid-cols-5 gap-2">
                    {LEAD_STATUSES.map((s) => (
                        <button
                            key={s}
                            type="button"
                            onClick={() => setFilterStatus(filterStatus === s ? "all" : s)}
                            data-testid={`pipeline-${s}`}
                            className={`rounded-xl p-3 text-center transition-all ring-1 ${
                                filterStatus === s
                                    ? "ring-brand-green shadow-pop -translate-y-0.5"
                                    : "ring-brand-green/10 hover:ring-brand-green/40"
                            } ${LEAD_STATUS_STYLES[s]}`}
                        >
                            <div className="text-[10px] uppercase font-bold tracking-widest opacity-80">
                                {LEAD_STATUS_LABELS[s]}
                            </div>
                            <div className="mt-1 font-display font-medium text-2xl">
                                {pipelineCounts[s] || 0}
                            </div>
                        </button>
                    ))}
                </div>
            </section>

            {/* Charts */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded-2xl bg-white p-6 shadow-soft ring-1 ring-brand-green/10">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-green">
                        <Package className="h-3.5 w-3.5" /> Leads por linha de produto
                    </div>
                    <div className="mt-4 space-y-2">
                        {Object.keys(LINE_COLORS).map((l) => {
                            const count = stats?.by_line?.[l] ?? 0;
                            const max = Math.max(1, ...Object.values(stats?.by_line || { x: 1 }));
                            const pct = Math.round((count / max) * 100);
                            return (
                                <div key={l} data-testid={`stat-line-${l}`}>
                                    <div className="flex items-center justify-between text-xs font-semibold text-brand-ink-soft">
                                        <span className={LINE_COLORS[l].text}>
                                            {LINE_COLORS[l].label}
                                        </span>
                                        <span>{count}</span>
                                    </div>
                                    <div className="mt-1 h-2 rounded-full bg-brand-cream overflow-hidden">
                                        <div
                                            className={`h-full rounded-full ${LINE_COLORS[l].text.replace("text-", "bg-")}`}
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="rounded-2xl bg-white p-6 shadow-soft ring-1 ring-brand-green/10">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-orange">
                        <Briefcase className="h-3.5 w-3.5" /> Leads por tipo de negócio
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {stats?.by_business && Object.keys(stats.by_business).length > 0 ? (
                            Object.entries(stats.by_business)
                                .sort((a, b) => b[1] - a[1])
                                .map(([b, c]) => (
                                    <div
                                        key={b}
                                        data-testid={`stat-business-${b.toLowerCase().replace(/\s/g, "-")}`}
                                        className="inline-flex items-center gap-1.5 rounded-full bg-brand-cream px-3 py-1.5 text-xs font-semibold text-brand-ink"
                                    >
                                        <span>{b}</span>
                                        <span className="h-5 min-w-[20px] px-1.5 rounded-full bg-brand-ink text-white text-[10px] inline-flex items-center justify-center">
                                            {c}
                                        </span>
                                    </div>
                                ))
                        ) : (
                            <div className="text-xs text-brand-ink-soft">
                                Ainda sem dados de tipo de negócio.
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Filters */}
            <section className="flex items-center gap-3 flex-wrap">
                <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-brand-ink-soft">
                    <Filter className="h-3.5 w-3.5" /> Filtros:
                </div>
                <Select
                    value={filterInterest}
                    onChange={setFilterInterest}
                    testid="filter-interest"
                    options={[
                        { v: "all", l: "Todos os interesses" },
                        { v: "parceiro", l: "Parceiros" },
                        { v: "consumidor", l: "Consumidores" },
                    ]}
                />
                <Select
                    value={filterLine}
                    onChange={setFilterLine}
                    testid="filter-line"
                    options={[
                        { v: "all", l: "Todas as linhas" },
                        { v: "fruit", l: "FRUIT" },
                        { v: "detox", l: "DETOX" },
                        { v: "protein", l: "PROTEIN" },
                        { v: "tea", l: "TEA" },
                    ]}
                />
                <Select
                    value={filterStatus}
                    onChange={setFilterStatus}
                    testid="filter-status"
                    options={[
                        { v: "all", l: "Todos os estados" },
                        ...LEAD_STATUSES.map((s) => ({ v: s, l: LEAD_STATUS_LABELS[s] })),
                    ]}
                />
                <button
                    type="button"
                    onClick={load}
                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-cream hover:bg-brand-green-soft text-brand-ink px-3 py-2 text-xs font-semibold transition-colors ml-auto"
                    data-testid="admin-refresh"
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
                    Atualizar
                </button>
            </section>

            {/* Leads list */}
            <section className="rounded-2xl bg-white shadow-soft ring-1 ring-brand-green/10 overflow-hidden">
                <div className="px-6 py-4 border-b border-brand-green/10 flex items-center justify-between">
                    <h2 className="font-display font-medium text-lg text-brand-ink">
                        Leads ({leads.length})
                    </h2>
                </div>

                {busy ? (
                    <div className="p-16 flex justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-brand-green" />
                    </div>
                ) : leads.length === 0 ? (
                    <div className="p-16 text-center">
                        <Users className="h-10 w-10 text-brand-green/40 mx-auto" />
                        <p className="mt-4 text-sm text-brand-ink-soft">
                            Ainda não há leads com estes filtros.
                        </p>
                    </div>
                ) : (
                    <ul className="divide-y divide-brand-green/10">
                        {leads.map((lead) => (
                            <li
                                key={lead.id}
                                data-testid={`lead-row-${lead.id}`}
                                className="px-6 py-4 hover:bg-brand-cream/40 transition-colors flex flex-col md:flex-row md:items-start gap-3"
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-bold text-brand-ink">
                                            {lead.name}
                                        </span>
                                        <span
                                            className={`text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 ${
                                                lead.interest === "parceiro"
                                                    ? "bg-brand-green text-white"
                                                    : "bg-brand-orange text-white"
                                            }`}
                                        >
                                            {lead.interest}
                                        </span>
                                        {lead.line_interest && (
                                            <span
                                                className={`text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 ${LINE_COLORS[lead.line_interest]?.bg} ${LINE_COLORS[lead.line_interest]?.text}`}
                                            >
                                                {LINE_COLORS[lead.line_interest]?.label}
                                            </span>
                                        )}
                                        {lead.business_type && (
                                            <span className="text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 bg-brand-cream text-brand-ink">
                                                {lead.business_type}
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-brand-ink-soft">
                                        <a
                                            href={`mailto:${lead.email}`}
                                            className="inline-flex items-center gap-1 hover:text-brand-green"
                                        >
                                            <Mail className="h-3 w-3" />
                                            {lead.email}
                                        </a>
                                        {lead.phone && (
                                            <span className="inline-flex items-center gap-1">
                                                <Phone className="h-3 w-3" />
                                                {lead.phone}
                                            </span>
                                        )}
                                        {lead.city && (
                                            <span className="inline-flex items-center gap-1">
                                                <MapPin className="h-3 w-3" />
                                                {lead.city}
                                            </span>
                                        )}
                                        <span className="inline-flex items-center gap-1">
                                            <Calendar className="h-3 w-3" />
                                            {formatDate(lead.created_at)}
                                        </span>
                                        {lead.follow_up_date && (
                                            <span
                                                className="inline-flex items-center gap-1 rounded-full bg-brand-yellow px-2 py-0.5 text-[10px] font-bold text-[#8a6b00]"
                                                data-testid={`lead-followup-${lead.id}`}
                                            >
                                                <Calendar className="h-3 w-3" />
                                                Seguir: {lead.follow_up_date}
                                            </span>
                                        )}
                                    </div>
                                    {lead.message && (
                                        <p className="mt-2 text-xs text-brand-ink-soft line-clamp-2 bg-brand-cream/60 rounded-xl px-3 py-2">
                                            {lead.message}
                                        </p>
                                    )}
                                    {lead.notes && (
                                        <p className="mt-2 text-xs text-brand-ink line-clamp-2 bg-brand-green-soft/50 rounded-xl px-3 py-2">
                                            <b>Nota interna:</b> {lead.notes}
                                        </p>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 md:flex-col md:items-end">
                                    <select
                                        value={lead.status || "novo"}
                                        onChange={(e) =>
                                            updateLead(lead.id, { status: e.target.value })
                                        }
                                        data-testid={`lead-status-select-${lead.id}`}
                                        className={`rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest border-none focus:outline-none focus:ring-2 focus:ring-brand-green cursor-pointer ${
                                            LEAD_STATUS_STYLES[lead.status || "novo"]
                                        }`}
                                    >
                                        {LEAD_STATUSES.map((s) => (
                                            <option key={s} value={s}>
                                                {LEAD_STATUS_LABELS[s]}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        onClick={() => setEditingId(lead.id)}
                                        data-testid={`lead-edit-notes-${lead.id}`}
                                        className="inline-flex items-center gap-1 rounded-full bg-brand-cream hover:bg-brand-green-soft text-brand-ink px-3 py-1.5 text-[11px] font-semibold transition-colors"
                                    >
                                        <Pencil className="h-3 w-3" />
                                        Notas
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {editingId && (
                <LeadNotesModal
                    lead={leads.find((l) => l.id === editingId)}
                    onClose={() => setEditingId(null)}
                    onSave={async (patch) => {
                        await updateLead(editingId, patch);
                        setEditingId(null);
                    }}
                />
            )}
        </div>
    );
}

const LeadNotesModal = ({ lead, onClose, onSave }) => {
    const [notes, setNotes] = useState(lead?.notes || "");
    const [followUp, setFollowUp] = useState(lead?.follow_up_date || "");
    const [saving, setSaving] = useState(false);

    if (!lead) return null;

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave({ notes, follow_up_date: followUp });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="lead-notes-modal"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6"
            >
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display font-medium text-lg text-brand-ink">
                        Notas · {lead.name}
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-brand-cream"
                        data-testid="lead-notes-close"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="text-xs uppercase tracking-widest font-bold text-brand-ink-soft">
                            Data de follow-up
                        </label>
                        <input
                            type="date"
                            value={followUp}
                            onChange={(e) => setFollowUp(e.target.value)}
                            data-testid="lead-notes-followup"
                            className="mt-1 w-full rounded-xl border-2 border-brand-green/15 focus:border-brand-green px-4 py-2.5 text-sm font-medium text-brand-ink focus:outline-none"
                        />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest font-bold text-brand-ink-soft">
                            Notas internas
                        </label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={5}
                            placeholder="Ex.: contactado por telefone, aguarda proposta..."
                            data-testid="lead-notes-textarea"
                            className="mt-1 w-full rounded-xl border-2 border-brand-green/15 focus:border-brand-green px-4 py-2.5 text-sm text-brand-ink focus:outline-none"
                        />
                    </div>
                </div>
                <div className="mt-6 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-full bg-brand-cream hover:bg-brand-green-soft text-brand-ink px-4 py-2 text-xs font-bold uppercase tracking-widest"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        data-testid="lead-notes-save"
                        className="inline-flex items-center gap-1.5 rounded-full bg-brand-green hover:bg-brand-green-dark text-white px-4 py-2 text-xs font-bold uppercase tracking-widest disabled:opacity-60"
                    >
                        <Save className="h-3.5 w-3.5" />
                        {saving ? "A guardar..." : "Guardar"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const StatCard = ({ icon: Icon, label, value, accent, bg, white, testid }) => (
    <div
        data-testid={testid}
        className={`rounded-2xl ${bg} p-5 md:p-6 shadow-soft ring-1 ring-brand-green/5`}
    >
        <Icon className={`h-5 w-5 ${accent}`} />
        <div
            className={`mt-3 text-[11px] uppercase tracking-widest font-bold ${white ? "text-white/80" : "text-brand-ink-soft"}`}
        >
            {label}
        </div>
        <div
            className={`mt-1 font-display font-medium text-3xl md:text-4xl ${white ? "text-white" : "text-brand-ink"}`}
        >
            {value}
        </div>
    </div>
);

const Select = ({ value, onChange, options, testid }) => (
    <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="rounded-full bg-white border-2 border-brand-green/15 px-4 py-2 text-xs font-semibold text-brand-ink focus:outline-none focus:border-brand-green"
    >
        {options.map((o) => (
            <option key={o.v} value={o.v}>
                {o.l}
            </option>
        ))}
    </select>
);

const formatDate = (iso) => {
    try {
        return new Intl.DateTimeFormat("pt-PT", {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        }).format(new Date(iso));
    } catch {
        return iso;
    }
};
